import type { YaasPositionCardData } from "@solomon-apps/shared/src/types/yaas";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import type { RaydiumLpHolding } from "@/hooks/yaas/lpTypes";
import { unmatchedLpHoldings } from "@/hooks/yaas/raydiumLpHoldings";

import { balancePositions } from "./balancePositions";
import { isRetailSelfHeldPosition } from "./isRetailSelfHeldPosition";

export interface RetailBalanceTotal {
    amount: BigDecimal;
    /** LP rows whose amount is unknown. `amount` leaves them out, so it is a
     *  lower bound until this is 0. */
    unresolved: number;
}

/**
 * Count wallet USDv once plus usable observed LP principal, regardless of discovery coverage.
 * One unreadable LP does not void the rest: it is counted in `unresolved` instead.
 * null only when the wallet amount or the whole LP observation is missing.
 */
export function retailBalanceTotal(
    positions: YaasPositionCardData[],
    walletAmount: string | null,
    holdings: RaydiumLpHolding[],
    balancesUnavailable: boolean
): RetailBalanceTotal | null {
    if (balancesUnavailable || walletAmount === null) return null;
    const amounts = [
        ...balancePositions(positions)
            .filter(({ pool }) => !isRetailSelfHeldPosition(pool))
            .map(({ position }) =>
                position.usdvAmountExact === undefined ? String(position.USDvValue) : position.usdvAmountExact
            ),
        ...unmatchedLpHoldings(holdings, positions).map((holding) => holding.amount)
    ];
    let amount = new BigDecimal(walletAmount);
    let unresolved = 0;
    for (const value of amounts) {
        if (value === null) unresolved += 1;
        else amount = amount.add(new BigDecimal(value));
    }
    return { amount, unresolved };
}

/** A partial total is a lower bound: reaching the minimum proves eligibility, falling short proves nothing. */
export function qualifyingAmount(total: RetailBalanceTotal | null, minBalance: BigDecimal): BigDecimal | null {
    if (total === null) return null;
    return total.unresolved === 0 || total.amount.gte(minBalance) ? total.amount : null;
}
